/**
 * Agent logs in words (routes/platform/agent-logs.tsx): why a bundle was sent, the agent's connection check and what
 * it found (DNS, the connection, the secure connection and who issued the certificate, the PC's clock, the proxy, a
 * web filter), its health, its online history, the bundle's file name, the groups the page shows, and the API's
 * refusals. Pure, but downloadAgentLog (a fetch, then a file the browser saves): the page and the tests call these.
 *
 * The report is the agent's own diagnostics.json (tools/ingest-agent-rs, engine/diag.rs and engine/check.rs), so every
 * field may be missing; what is missing is left out, never guessed.
 */
import {
  type AgentCertInfo,
  type AgentConnectionCheck,
  type AgentDiagnosticsReport,
  type AgentLogBundle,
  type AgentLogReason,
  type AgentPresenceEvent,
  type AgentProblem,
  type IngestAgent,
  agentLogs,
} from "@/lib/api/ingest-agents";
import { errorCodeOf, isNetworkError, statusOf } from "@/lib/api/error";
import { fmtWhen, sortAgents } from "@/lib/plant-agents";

/** The org's PCs (the Plant agents page reads them), as the Logs tab is told of them. */
export type AgentsState = "loading" | "error" | "ready";

// ── Why a bundle was sent ────────────────────────────────────────────────────

/** A reason in a word or two, for the list, and in a sentence, for its title. */
export const REASON_WORDS: Record<AgentLogReason, { label: string; title: string }> = {
  daily: { label: "Daily", title: "Sent once a day, when anything was logged" },
  problem: { label: "After a problem", title: "Sent after an outage of 2 minutes or more, or a failed update" },
  request: { label: "Asked", title: "Sent because a platform admin asked for the PC's logs" },
  manual: { label: "By hand", title: "Someone at the PC pressed Send logs" },
  explore: {
    label: "Explore",
    title: "The documents of an explore run: sent after an admin asked for it, or saved to a pendrive and uploaded",
  },
};

/** A length of time in seconds, in words: "45 s", "12 min", "2 h 5 min", "3 days". */
export function durationWords(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return "";
  if (secs < 60) return `${Math.round(secs)} s`;
  const minutes = Math.floor(secs / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  return `${Math.floor(hours / 24)} days`;
}

/** A moment the agent wrote in Unix seconds, as the page shows moments. */
export function fmtUnix(secs: number | null | undefined): string {
  return typeof secs === "number" && Number.isFinite(secs) ? fmtWhen(new Date(secs * 1000).toISOString()) : "";
}

/** The agent's last update outcome ("failed:0.3.2:why"), read. */
export function updateOutcome(raw: string | null | undefined): { state: string; version: string; why: string } | null {
  if (!raw) return null;
  const [state, version = "", ...why] = raw.split(":");
  return state ? { state, version, why: why.join(":").trim() } : null;
}

/**
 * Why a bundle was sent, in a sentence. A bundle sent after a problem says which, when its report tells: an outage
 * (the agent had one whose logs were not sent yet) or a failed update (its last update did not go in).
 */
export function reasonSentence(reason: AgentLogReason, report?: AgentDiagnosticsReport | null): string {
  if (reason !== "problem") return REASON_WORDS[reason]?.title ?? reason;
  const parts: string[] = [];
  const outage = report?.health?.last_outage;
  if (report?.health?.outage_unsent && Array.isArray(outage)) {
    parts.push(`an outage of ${durationWords(outage[0])}, which ended ${fmtUnix(outage[1])}`);
  }
  const up = updateOutcome(report?.update?.last_outcome);
  if (up && up.state !== "updated") {
    parts.push(
      up.state === "rolled_back"
        ? `an update to ${up.version} that was rolled back${up.why ? ` (${up.why})` : ""}`
        : `a failed update to ${up.version}${up.why ? ` (${up.why})` : ""}`,
    );
  }
  return parts.length ? `Sent after ${parts.join(", and after ")}` : REASON_WORDS.problem.title;
}

// ── The connection check ─────────────────────────────────────────────────────

export type CheckTone = "ok" | "fail" | "skip";

/** A check's name in plain words: the agent labels them "Look up <host>", "Connect to port 443" and so on. */
export function checkName(label: string): string {
  if (label === "This PC's address") return "This PC's network address";
  if (label.startsWith("Look up ")) return `Name lookup (DNS) of ${label.slice("Look up ".length)}`;
  if (label.startsWith("Connect to port ")) return `Connection to port ${label.slice("Connect to port ".length)}`;
  if (label === "Secure connection") return "Secure connection (TLS)";
  if (label === "Sahas Forge health check") return "Sahas Forge answers";
  if (label === "Sahas Forge address") return "Sahas Forge's address";
  return label;
}

/** One check in words: its name, how it went, and what the agent saw. */
export function checkWords(c: AgentConnectionCheck): { name: string; outcome: string; tone: CheckTone; detail: string } {
  const name = checkName(c.label);
  switch (c.result) {
    case "ok":
      return { name, outcome: "Passed", tone: "ok", detail: c.detail };
    case "fail":
      return { name, outcome: "Failed", tone: "fail", detail: c.detail };
    default:
      return { name, outcome: "Not tried", tone: "skip", detail: "an earlier step failed" };
  }
}

/** The certificate a problem names, or the one the check saw. */
function certOf(p: AgentProblem | null | undefined, fallback?: AgentCertInfo | null): AgentCertInfo | null {
  return p?.certificate ?? fallback ?? null;
}

/**
 * What is wrong, in a headline per kind of problem (the agent's own title and advice go under it). Intercepted names
 * who issued the certificate the network presented: often the firewall's maker.
 */
export function problemHeadline(p: AgentProblem, cert?: AgentCertInfo | null): string {
  const issuer = certOf(p, cert)?.issuer;
  switch (p.code) {
    case "dns":
      return "The name lookup (DNS) failed: this PC gets no address for Sahas Forge";
    case "timeout":
      return "No answer on port 443: a firewall is most likely dropping the connection";
    case "refused":
      return "The connection was refused: a firewall rule rejects it";
    case "reset":
      return "The connection was cut as soon as it named the site: a firewall that filters by name";
    case "intercepted":
      return issuer
        ? `The secure connection is intercepted: the network answered with its own certificate, issued by ${issuer}`
        : "The secure connection is intercepted: the network answered with its own certificate";
    case "clock":
      return "The certificate's dates do not fit this PC's clock";
    case "proxy_auth":
      return "A proxy on the network wants a sign-in";
    case "web_filter":
      return "A web filter or sign-in page answered instead of Sahas Forge";
    case "not_sahas":
      return "Something else on the network answered instead of Sahas Forge";
    case "tls":
      return "The secure connection failed";
    case "unreachable":
      return "Sahas Forge can't be reached from this PC";
    default:
      return p.title || "A connection problem";
  }
}

/** The connection check's verdict in a sentence. Null when the report has none. */
export function checkVerdict(report: AgentDiagnosticsReport): { ok: boolean; text: string } | null {
  const cc = report.connection_check;
  if (!cc) return null;
  if (cc.direct) return { ok: true, text: "The connection check passed: this PC reaches Sahas Forge directly." };
  const p = cc.problem;
  return {
    ok: false,
    text: p
      ? `The connection check failed. ${problemHeadline(p, cc.certificate)}.`
      : "The connection check failed.",
  };
}

/** The Windows proxy in words. The agent connects directly, without it. */
export function proxyWords(report: AgentDiagnosticsReport): string {
  if (!report.proxy) return "Windows on this PC uses no proxy.";
  return `Windows on this PC is set to use ${report.proxy}. The agent connects directly, without it.`;
}

/** A certificate in words: who it is for, who issued it, and its dates. */
export function certWords(c: AgentCertInfo): string {
  const dates = c.not_before && c.not_after ? `, valid ${c.not_before} to ${c.not_after}` : "";
  return `${c.subject}, issued by ${c.issuer}${dates}`;
}

/** The way an agent reached the cloud: "directly" or "through gateway <id>". */
export function viaWords(via: string | null | undefined): string {
  if (!via) return "";
  return via === "direct" ? "directly" : `through ${via}`;
}

/** The agent's health when it made the report: online since when and how, or offline since when and why. */
export function healthWords(report: AgentDiagnosticsReport): { tone: "ok" | "fail" | "skip"; text: string } {
  const h = report.health;
  if (!h || h.online === null || h.online === undefined) {
    return { tone: "skip", text: "It had not tried to reach Sahas Forge yet." };
  }
  const since = fmtUnix(h.since);
  if (h.online) {
    const via = viaWords(h.via);
    return { tone: "ok", text: `Online${via ? ` ${via}` : ""}${since ? ` since ${since}` : ""}.` };
  }
  return {
    tone: "fail",
    text: `Offline${since ? ` since ${since}` : ""}${h.problem ? `: ${problemHeadline(h.problem)}` : ""}.`,
  };
}

/** The last outage that ended, in words, or "". */
export function lastOutageWords(report: AgentDiagnosticsReport): string {
  const o = report.health?.last_outage;
  if (!Array.isArray(o) || o.length < 2) return "";
  return `${durationWords(o[0])} offline, which ended ${fmtUnix(o[1])}`;
}

/** The agent's last update in words, or "". */
export function updateWordsOf(report: AgentDiagnosticsReport): string {
  const up = updateOutcome(report.update?.last_outcome);
  if (!up) return "";
  const why = up.why ? ` (${up.why})` : "";
  if (up.state === "updated") return `Updated to ${up.version}`;
  if (up.state === "rolled_back") return `Rolled back from ${up.version}${why}`;
  return `The update to ${up.version} failed${why}`;
}

// ── The online history ───────────────────────────────────────────────────────

/**
 * An online history's lines in words, newest first as the API gives them. A return online says how long the PC was
 * offline, from the offline line before it.
 */
export function presenceEventWords(events: AgentPresenceEvent[]): { at: string; text: string; tone: "ok" | "fail" | "skip" }[] {
  return events.map((e, i) => {
    const via = viaWords(e.via);
    switch (e.event) {
      case "online": {
        const before = events.slice(i + 1).find((x) => x.event === "online" || x.event === "offline");
        const gone = before?.event === "offline" ? (Date.parse(e.at) - Date.parse(before.at)) / 1000 : NaN;
        const back = Number.isFinite(gone) && gone >= 0 ? `, back after ${durationWords(gone)} offline` : "";
        return { at: e.at, tone: "ok", text: `Online${via ? ` ${via}` : ""}${back}` };
      }
      case "offline":
        return { at: e.at, tone: "fail", text: "Offline (last heard from)" };
      case "offline_long":
        return { at: e.at, tone: "fail", text: "Still offline after 30 min" };
      case "paused":
        return { at: e.at, tone: "skip", text: "Paused by someone at the PC" };
      case "resumed":
        return { at: e.at, tone: "ok", text: "Resumed at the PC" };
      default:
        return { at: e.at, tone: "skip", text: String(e.event) };
    }
  });
}

// ── The bundles ──────────────────────────────────────────────────────────────

/** The zip's name as the API's download names it: SFA-XXXX-XXXX-20261010-080000-problem.zip (UTC). */
export function bundleFileName(b: Pick<AgentLogBundle, "identity_id" | "created_at" | "reason">): string {
  const d = new Date(b.created_at);
  const stamp = Number.isNaN(d.getTime())
    ? "bundle"
    : d.toISOString().replace(/[-:]/g, "").replace("T", "-").slice(0, 15);
  return `${b.identity_id}-${stamp}-${b.reason}.zip`;
}

/** Bundles grouped by agent, each group's newest first, the group with the newest bundle first. */
export function groupByAgent(bundles: AgentLogBundle[]): { agentId: string; bundles: AgentLogBundle[] }[] {
  const groups = new Map<string, AgentLogBundle[]>();
  for (const b of [...bundles].sort((x, y) => y.created_at.localeCompare(x.created_at))) {
    const g = groups.get(b.agent_id);
    if (g) g.push(b);
    else groups.set(b.agent_id, [b]);
  }
  return [...groups].map(([agentId, list]) => ({ agentId, bundles: list }));
}

/** One PC's bundles: the agent when it is the active org's (for its presence and history), else what the list says. */
export interface AgentLogGroup {
  agentId: string;
  name: string;
  identityId: string;
  orgId: string;
  /** The plant's name, from the list (the API joins it); null when the org row is gone, or the API is older. */
  orgName: string | null;
  agent?: IngestAgent;
  bundles: AgentLogBundle[];
}

/** A plant for the list: its name, or its short id when the org has no name (its row is gone). */
export function plantLabel(orgId: string, name: string | null | undefined): string {
  return name ? name : `org ${orgId.slice(0, 8)}`;
}

/** Which PCs' bundles the Logs tab shows: the active org's, or every org's. */
export type LogsScope = "org" | "all";

/**
 * The groups to show: in the org scope every PC of the org that is connected or sent a bundle (the ones with the
 * newest bundles first), in the every-org scope every PC that sent a bundle. A PC of the active org carries its agent.
 */
export function logGroups(
  scope: LogsScope,
  agents: IngestAgent[],
  bundles: AgentLogBundle[],
  activeOrgId: string | null,
): AgentLogGroup[] {
  const byId = new Map(agents.map((a) => [a.id, a]));
  const seen = new Set<string>();
  const out: AgentLogGroup[] = groupByAgent(bundles).map(({ agentId, bundles: list }) => {
    seen.add(agentId);
    const a = byId.get(agentId);
    const first = list[0];
    return {
      agentId,
      name: a?.name ?? first.agent_name,
      identityId: first.identity_id,
      orgId: first.org_id,
      orgName: first.org_name ?? null,
      agent: a,
      bundles: list,
    };
  });
  if (scope === "org") {
    for (const a of sortAgents(agents)) {
      if (a.status === "active" && !seen.has(a.id)) {
        out.push({
          agentId: a.id,
          name: a.name,
          identityId: a.identity_id,
          orgId: activeOrgId ?? "",
          orgName: null,
          agent: a,
          bundles: [],
        });
      }
    }
  }
  return out;
}

/**
 * Whether a PC asked for its logs has sent them: a bundle sent because it was asked, that was not in the list when the
 * ask was made. The browser's clock is never compared with the server's.
 */
export function answeredBundle(
  bundles: AgentLogBundle[],
  agentId: string,
  known: ReadonlySet<string>,
): AgentLogBundle | null {
  return bundles.find((b) => b.agent_id === agentId && b.reason === "request" && !known.has(b.id)) ?? null;
}

/**
 * How long an ask is waited for. A PC that is on and online sends within seconds of a sync, and one that is offline
 * sends once it is back: past this the page stops reading the list every 10 s for it and says no logs came.
 */
export const ASK_WAIT_MS = 10 * 60_000;

/** "Ask for logs now", as a PC's group shows it: being sent, waiting for the PC, given up on, or answered with a bundle. */
export type AskState =
  | { state: "sending" }
  | { state: "pending"; at: number; offline: boolean }
  | { state: "expired"; at: number }
  | { state: "done"; bundle: AgentLogBundle };

/** An ask in flight or waiting: when it was made (the browser's clock), and the bundles sent on request listed then. */
export type Ask = { state: "sending" } | { state: "pending"; at: number; offline: boolean; known: ReadonlySet<string> };

/**
 * What each PC's ask shows, against the list read last: answered once a new bundle sent on request is in it, and given
 * up on once ASK_WAIT_MS has passed (at `now`, the browser's clock) without one. A bundle that comes after that still
 * answers it.
 */
export function askStates(
  asks: Record<string, Ask>,
  items: AgentLogBundle[],
  now: number = Date.now(),
): Record<string, AskState> {
  const out: Record<string, AskState> = {};
  for (const [agentId, a] of Object.entries(asks)) {
    if (a.state === "sending") {
      out[agentId] = a;
      continue;
    }
    const got = answeredBundle(items, agentId, a.known);
    if (got) out[agentId] = { state: "done", bundle: got };
    else if (now - a.at >= ASK_WAIT_MS) out[agentId] = { state: "expired", at: a.at };
    else out[agentId] = { state: "pending", at: a.at, offline: a.offline };
  }
  return out;
}

/** Whether any ask is still being waited for: the list is read every 10 s while so. */
export function anyWaiting(asks: Record<string, Ask>, items: AgentLogBundle[], now: number = Date.now()): boolean {
  return Object.values(askStates(asks, items, now)).some((a) => a.state === "pending");
}

/**
 * Whether a PC can be asked for its logs, and when not why, in words (shown where the button would be). The request is
 * not org-scoped, so in the every-org scope a PC the org's list does not hold can be asked; in the org scope it can only
 * be when the org's list holds it, so the list loading or failing is said, not hidden.
 */
export function askAvailability(
  agent: IngestAgent | undefined,
  scope: LogsScope,
  agentsState: AgentsState,
): { can: true } | { can: false; reason: string } {
  if (agent) {
    if (agent.status === "active") return { can: true };
    return { can: false, reason: agent.status === "revoked" ? "Revoked: it can't send logs." : "Not set up on a PC yet." };
  }
  if (scope === "all") return { can: true };
  if (agentsState === "loading") return { can: false, reason: "Loading this org's PCs." };
  if (agentsState === "error") return { can: false, reason: "Couldn't load this org's PCs, so it can't be asked." };
  return { can: false, reason: "Not in this org's list of PCs." };
}

/**
 * What a PC's online history can say when the PC is not among the org's PCs read: loading or failed (the history is the
 * org's, so it can't be read yet), or in another org (the history shows for the org you are in). Null when it is.
 */
export function historyBlocked(
  hasAgent: boolean,
  agentsState: AgentsState,
): { state: "loading" | "error" | "elsewhere"; error?: string } | null {
  if (hasAgent) return null;
  if (agentsState === "loading") return { state: "loading" };
  if (agentsState === "error") {
    return { state: "error", error: "Couldn't load this org's PCs, so the online history can't be read." };
  }
  return { state: "elsewhere" };
}

/** Saves a blob as a file the browser downloads. */
function saveBlob(blob: Blob, name: string) {
  if (typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Downloads a bundle: an authenticated fetch of the zip, saved by the browser under the API's name for it. An empty
 * answer is refused, not saved: an API before the #1339 fix answers a bundle the store no longer has with an empty 200.
 * So is one that is not the size the list says the bundle is (a connection cut short saves a zip that won't open).
 */
export async function downloadAgentLog(
  b: AgentLogBundle,
  save: (blob: Blob, name: string) => void = saveBlob,
): Promise<void> {
  const blob = await agentLogs.download(b.id);
  if (blob.size === 0) throw new EmptyBundleError();
  if (blob.size !== b.size) throw new IncompleteBundleError(blob.size, b.size);
  save(blob, bundleFileName(b));
}

/** A download that is not the size of the bundle the list says: cut short, or not the bundle. Nothing is saved. */
export class IncompleteBundleError extends Error {
  constructor(got: number, want: number) {
    super(`The download does not match the bundle: ${got} bytes came, ${want} were expected. Nothing was saved. Try again.`);
    this.name = "IncompleteBundleError";
  }
}

/** A download that came back empty: the store no longer has the bundle. */
export class EmptyBundleError extends Error {
  constructor() {
    super("This bundle is no longer stored: the download came back empty.");
    this.name = "EmptyBundleError";
  }
}

// ── Refusals ─────────────────────────────────────────────────────────────────

/** What went wrong with an agent logs call, in words. */
export function logsErrorWords(err: unknown, fallback: string): string {
  if (err instanceof EmptyBundleError || err instanceof IncompleteBundleError) return err.message;
  const status = statusOf(err);
  const code = errorCodeOf(err);
  if (status === 403) return "Only a platform admin can see agent logs.";
  if (code === "logs_not_stored") return "This bundle is no longer stored. Its row remains, but its file is gone.";
  if (code === "logs_not_found") return "This bundle no longer exists.";
  if (code === "diagnostics_not_found") return "This bundle holds no diagnostics report, only logs. Download it to read them.";
  if (code === "logs_unconfigured") return "This Sahas Forge keeps no agent logs: no logs store is set up.";
  if (code === "agent_not_found") return "This agent no longer exists.";
  if (status === 422) return "The bundle can't be read here. Download it to look inside.";
  if (status === 502) return "The logs store did not answer. Try again in a minute.";
  if (isNetworkError(err)) return "The platform could not be reached. Check the connection and try again.";
  return fallback;
}
